import { useMemo } from 'react';
import { Spinner } from '@monksflow/monks-ui';
import { DownloadIcon } from 'lucide-react';
import type { WidgetPanelProps } from '@/shared/ui/SidePanelWrapper';
import { SPECIAL_AGENT_IDS, useAgentCategoryChipColor } from '@/entities/agent';
import { useTranslation } from 'react-i18next';

interface UseOpportunityHeaderConfigParams {
  detailsView: boolean;
  handleClose: () => void;
  handleDownload: () => void;
  handleCreateBrief: () => void;
  canDownload: boolean;
  isDownloading: boolean;
  isWaiting: boolean;
  /**
   * Library preview mount: hide the "Generate Brief" CTA (it navigates away
   * via sendOrCreateChat). Download is unaffected; it shows whenever `canDownload` is true.
   */
  readOnly?: boolean;
}

export function useOpportunityHeaderConfig(params: UseOpportunityHeaderConfigParams) {
  const { t } = useTranslation('analysis');

  const {
    detailsView,
    handleClose,
    handleDownload,
    handleCreateBrief,
    canDownload,
    isDownloading,
    isWaiting,
    readOnly,
  } = params;

  const categoryChipColor = useAgentCategoryChipColor(
    SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER,
  );

  return useMemo<WidgetPanelProps['headerConfig']>(() => {
    if (detailsView) {
      return {
        // Read-only preview opens straight into the primary opportunity's
        // detail and forbids list browsing, so there's nowhere to go "Back" to.
        ...(readOnly
          ? {}
          : {
              backButton: {
                label: t('socialMediaOpportunitiesWidget.useOpportunityHeaderConfig.back'),
                onClick: handleClose,
              },
            }),
        actions: [
          ...(canDownload
            ? [
                {
                  type: 'icon' as const,
                  icon: isDownloading ? <Spinner size="lg" /> : <DownloadIcon />,
                  onClick: handleDownload,
                  tooltip: t(
                    'socialMediaOpportunitiesWidget.useOpportunityHeaderConfig.downloadOpportunity',
                  ),
                  ariaLabel: t(
                    'socialMediaOpportunitiesWidget.useOpportunityHeaderConfig.downloadOpportunity',
                  ),
                  disabled: isDownloading,
                },
              ]
            : []),
          ...(readOnly
            ? []
            : [
                {
                  type: 'button' as const,
                  label: t(
                    'socialMediaOpportunitiesWidget.useOpportunityHeaderConfig.generateBrief',
                  ),
                  onClick: handleCreateBrief,
                  // eslint-disable-next-line i18next/no-literal-string -- Button variant prop value
                  variant: 'contained' as const,
                  disabled: isWaiting,
                },
              ]),
        ],
        showDivider: true,
      };
    }
    return {
      chip: {
        label: t('socialMediaOpportunitiesWidget.useOpportunityHeaderConfig.opportunities'),
        color: categoryChipColor,
      },
    };
  }, [
    categoryChipColor,
    t,
    detailsView,
    handleClose,
    handleCreateBrief,
    isWaiting,
    handleDownload,
    canDownload,
    isDownloading,
    readOnly,
  ]);
}
